import { Image } from "@fluentui/react";
import React from "react";
import fullLogo from "../../media/full_logo.svg";
import smallLogo from "../../media/small_logo.svg";
import UserPersona from "./UserPersona";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import SidebarExpandIcon from "../../media/SidebarExpandIcon";
import SidebarCollapseIcon from "../../media/SidebarCollapseIcon";
import HomeIcon from "../../media/HomeIcon";
import BlogIcon from "../../media/BlogIcon";
import DashboardIcon from "../../media/DashboardIcon";
import QueueInsightsIcon from "../../media/QueueInsightsIcon";
import TransmissionIcon from "../../media/TransmissionIcon";
import MarketIcon from "../../media/MarketIcon";
import DataIcon from "../../media/DataIcon";
import ReferencesIcon from "../../media/ReferencesIcon";
import ThemeIcon from "../../media/ThemeIcon";
import SitemapIcon from "../../media/SitemapIcon";
import SettingsIcon from "../../media/SettingsIcon";
import NavTooltip from "./NavTooltip";
import NavDropdown from "./ThemeDropdown";
import { NAV_PANEL } from "../constants/uiConstants";

interface LinkProps {
    name: string;
    url: string;
    icon: any;
}

interface NavPanelProps {
    isOpen: boolean;
    onToggle: () => void;
}

const NavPanel: React.FC<NavPanelProps> = ({ isOpen, onToggle }) => {
    const navigate = useNavigate();
    const { theme, setTheme, currentTheme } = useTheme();

    const toggleNavbar = () => {
        onToggle();
    };

    const isLinkSelected = (url: string) => {
        return window.location.pathname === url;
    };

    const links: LinkProps[] = [
        {
            name: "Home",
            url: "/",
            icon: (
                <HomeIcon
                    isSelected={isLinkSelected("/")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "Blog",
            url: "/blog",
            icon: (
                <BlogIcon
                    isSelected={isLinkSelected("/blog")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "Dashboard",
            url: "/dashboard",
            icon: (
                <DashboardIcon
                    isSelected={isLinkSelected("/dashboard")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "Queue Insights",
            url: "/queue-insights",
            icon: (
                <QueueInsightsIcon
                    isSelected={isLinkSelected("/queue-insights")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "Transmission Insights",
            url: "/transmission-insights",
            icon: (
                <TransmissionIcon
                    isSelected={isLinkSelected("/transmission-insights")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "Market Insights",
            url: "/market-insights",
            icon: (
                <MarketIcon
                    isSelected={isLinkSelected("/market-insights")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "Data",
            url: "/data",
            icon: (
                <DataIcon
                    isSelected={isLinkSelected("/data")}
                    theme={currentTheme}
                />
            ),
        },
        {
            name: "References",
            url: "/references",
            icon: (
                <ReferencesIcon
                    isSelected={isLinkSelected("/references")}
                    theme={currentTheme}
                />
            ),
        },
    ];

    return (
        <div
            className={`relative transition-all duration-300 ease-in-out h-dvh bg-white dark:bg-[#2D3748] shadow-r-2 flex flex-col group z-50`}
            style={{
                width: isOpen ? `${NAV_PANEL.EXPANDED.WIDTH}px` : `${NAV_PANEL.COLLAPSED.WIDTH}px`
            }}
        >
            {/* Header Section */}
            <div
                className={`flex items-center p-[10px] ${
                    isOpen ? "justify-between mr-2" : "relative justify-center"
                }`}
            >
                {/* Full Logo */}
                {isOpen ? <Image src={fullLogo} /> : <Image src={smallLogo} />}

                {/* Toggle Button */}
                <div
                    className="cursor-pointer rounded-md"
                    onClick={toggleNavbar}
                >
                    {isOpen ? (
                        <SidebarCollapseIcon theme={currentTheme} />
                    ) : (
                        <div className="absolute -right-3 top-[18px] opacity-0 group-hover:opacity-100 duration-300">
                            <SidebarExpandIcon theme={currentTheme} />
                        </div>
                    )}
                </div>
            </div>

            <div
                className={`px-[10px] pt-0 flex h-[100vh] ${
                    !isOpen ? "flex justify-center w-full" : ""
                }`}
            >
                <nav className="flex flex-col gap-[16px] justify-between mt-[20px]">
                    <ul className="overflow-y-auto flex flex-col gap-[16px] [&::-webkit-scrollbar]:w-0 [-ms-overflow-style:none] [scrollbar-width:none]">
                        {links.map((link, index) => (
                            <li key={index} className="relative">
                                <div
                                    className={`flex cursor-pointer items-center gap-4 rounded-md px-1 group hover:bg-[#C6C5C5] dark:hover:bg-gray-700 duration-300 ${
                                        !isOpen ? "w-fit pt-1" : "py-1"
                                    }`}
                                    onClick={() => navigate(link.url)}
                                >
                                    {isLinkSelected(link.url) && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: "0px",
                                                top: "0px",
                                                width: "5px",
                                                height: "100%",
                                                backgroundColor: "#C6D92D",
                                                transition:
                                                    "background 0.3s ease",
                                                transform: "translateX(-10px)",
                                            }}
                                        ></div>
                                    )}
                                    {isOpen ? (
                                        <>
                                            {link.icon}
                                            <p
                                                className={`text-[15px] ${
                                                    isLinkSelected(link.url)
                                                        ? "text-black dark:text-white"
                                                        : "text-gray-700 dark:text-[#CEE0EF]"
                                                } duration-300`}
                                            >
                                                {link.name}
                                            </p>
                                            {link.url ===
                                                "/market-insights" && (
                                                <div className="right-4 px-[4px] py-[1px] rounded-[10px] border-[1.5px] border-[#C3C3C3] dark:border-[#CEE0EF] font-bold text-[10px] text-[#8B8B8B] bg-[#F1F1F1] dark:bg-[#2D3748] dark:text-[#CEE0EF]">
                                                    BETA
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <NavTooltip
                                            name={link.name}
                                            icon={link.icon}
                                        />
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="space-y-[16px] pb-4">
                        <NavDropdown
                            icon={<ThemeIcon theme={currentTheme} />}
                            toggleTheme={setTheme}
                            currentTheme={theme}
                            isOpen={isOpen}
                        />
                        <div className="flex px-1 gap-4 rounded-md py-1 cursor-pointer hover:bg-[#C6C5C5] dark:hover:bg-gray-700 duration-300">
                            {isOpen ? (
                                <>
                                    <SettingsIcon theme={currentTheme} />
                                    <p className="text-gray-700 dark:text-gray-300 text-[15px]">
                                        Settings
                                    </p>
                                </>
                            ) : (
                                <NavTooltip
                                    name="Settings"
                                    icon={<SettingsIcon theme={currentTheme} />}
                                />
                            )}
                        </div>
                        <div className="flex px-1 gap-4 rounded-md py-1 cursor-pointer hover:bg-[#C6C5C5] dark:hover:bg-gray-700 duration-300">
                            {isOpen ? (
                                <>
                                    <SitemapIcon theme={currentTheme} />
                                    <p className="text-gray-700 dark:text-gray-300 text-[15px]">
                                        Site Map
                                    </p>
                                </>
                            ) : (
                                <NavTooltip
                                    name="Site Map"
                                    icon={<SitemapIcon theme={currentTheme} />}
                                />
                            )}
                        </div>
                        <UserPersona isOpen={isOpen} />
                    </div>
                </nav>
            </div>
        </div>
    );
};

export default NavPanel;